/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo, useEffect, useCallback } from 'react';
import { Platform, AppState } from 'react-native';

import { useAppConfig } from '@airasia-rn/ui';
import { useSuperAppContext } from '@airasia-rn/ui/context';
import { jwtDecode } from '@airasia-rn/ui/utils';
import { useLogger } from '@airasia-rn/core-logger';

import {
  apiErrorMeta,
  classifyServiceError,
  errorTrackingEvent,
} from '../analytics';
import { useFlightConfig } from '../config/FlightConfigProvider';
import { AuthApiService } from '../services/authApiService';
import {
  AUTH_INVALID_RESPONSE_DESCRIPTION,
  AUTH_INVALID_RESPONSE_MESSAGE,
  AUTH_LOGIN_BACKED_OFF_DESCRIPTION,
  AUTH_LOGIN_BACKED_OFF_MESSAGE,
  AUTH_REQUEST_FAILED_DESCRIPTION,
  AUTH_REQUEST_FAILED_MESSAGE_PREFIX,
} from '../services/authApiErrors';
import { hasJwtExpired } from '@airasia-rn/account/managers';
import { flightKeys } from './flights';
import { getApiPlatform } from '../util/utils';

// Rule: hook offset < client buffer < server buffer.
const AUTH_TOKEN_REFRESH_OFFSET_SECONDS = 180;
// const AUTH_TOKEN_REFRESH_OFFSET_SECONDS = 870; // 30 seconds cadence for 15 min tokens
// const AUTH_TOKEN_REFRESH_OFFSET_SECONDS = 1170; // 30 seconds cadence for 20 min tokens
const AUTH_TOKEN_RETRY_DELAY_MS = 60 * 1000;
const AUTH_TOKEN_MIN_REFETCH_INTERVAL_MS = 5000;

/**
 * Milliseconds until the token should be refreshed (i.e. until `exp - offset`).
 * `baseTime` defaults to now, but scheduling callers pass the query's
 * `dataUpdatedAt` so the computed staleTime/refetchInterval stays frozen for a
 * given token — otherwise every React Query `setOptions` (after each render)
 * recomputes a smaller value and keeps resetting the refresh timer.
 */
function msUntilJwtRefresh(
  token: string,
  offsetSeconds: number,
  baseTime: number = Date.now(),
): number {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return (exp - offsetSeconds) * 1000 - baseTime;
  } catch {
    return 0;
  }
}

type AuthTokenResult =
  | string
  | {
      accessToken?: string;
    };

function resolveAccessToken(response: AuthTokenResult): string {
  if (typeof response === 'string') {
    return response;
  }

  if (response?.accessToken) {
    return response.accessToken;
  }

  throw new Error('Invalid authentication response');
}

export function useAuthApiService(): AuthApiService {
  const appConfig = useAppConfig();
  const {
    config: { auth, channelHash, geoId: flightConfigGeoId },
  } = useFlightConfig();
  const userAppContext = useSuperAppContext();

  const geoId =
    flightConfigGeoId || userAppContext.geoId || appConfig.config.geoId || 'MY';

  return useMemo(() => {
    return new AuthApiService(
      appConfig.config.env,
      geoId,
      auth?.username,
      auth?.password,
      channelHash,
    );
  }, [
    appConfig.config.env,
    geoId,
    auth?.username,
    auth?.password,
    channelHash,
  ]);
}

export const useAuthToken = (screenName: string = getApiPlatform()) => {
  const apiService = useAuthApiService();
  const { logError } = useLogger();

  const stableStaleTime = useCallback((currentQuery: any) => {
    const token = currentQuery.state.data;
    if (!token || typeof token !== 'string') return 0;
    return Math.max(
      0,
      msUntilJwtRefresh(
        token,
        AUTH_TOKEN_REFRESH_OFFSET_SECONDS,
        currentQuery.state.dataUpdatedAt || Date.now(),
      ),
    );
  }, []);

  const stableRefetchInterval = useCallback((currentQuery: any) => {
    if (currentQuery.state.status === 'error') {
      // Halt completely on consecutive failures to prevent DDOS-like loop
      return false;
    }

    const token = currentQuery.state.data;
    if (!token) return AUTH_TOKEN_RETRY_DELAY_MS;
    return Math.max(
      msUntilJwtRefresh(
        token,
        AUTH_TOKEN_REFRESH_OFFSET_SECONDS,
        currentQuery.state.dataUpdatedAt || Date.now(),
      ),
      AUTH_TOKEN_MIN_REFETCH_INTERVAL_MS,
    );
  }, []);

  const query = useQuery({
    queryKey: flightKeys.authtoken,

    queryFn: async (context) => {
      // Flights-scoped background pause (native only; web is already gated by
      // the browser's focusManager + refetchIntervalInBackground: false).
      // No focus sync is wired on native, so focusManager stays true in
      // background and interval ticks still execute — returning the cached
      // token here performs zero network I/O without mutating the global
      // focusManager, leaving every other query in the app untouched.
      // Fail-open: only states that are definitively not foreground pause;
      // 'unknown' (transient at launch) still fetches.
      const appState = AppState.currentState;
      if (
        Platform.OS !== 'web' &&
        (appState === 'background' || appState === 'inactive')
      ) {
        const cached = context.client.getQueryData<string>(
          flightKeys.authtoken,
        );
        if (typeof cached === 'string' && cached) return cached;
      }
      const token = await apiService.requestNewToken();
      return resolveAccessToken(token);
    },
    staleTime: stableStaleTime,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: stableRefetchInterval,
    refetchIntervalInBackground: false,
    retry: 1,
    retryDelay: 2000,
  });

  const { data: token } = query;

  const isTokenValid = token ? !hasJwtExpired(token) : false;

  useEffect(() => {
    const handleActiveState = () => {
      const currentToken = query.data;
      if (!currentToken) {
        // Let React Query handle initial loading on mount to avoid double-fetching
        return;
      }

      const timeRemaining = msUntilJwtRefresh(
        currentToken,
        AUTH_TOKEN_REFRESH_OFFSET_SECONDS,
      );
      if (timeRemaining <= 0) {
        query.refetch();
      }
    };

    // No mount-time call: React Query fetches on mount when stale (staleTime
    // is 0 with no token), so an explicit refetch here would only
    // double-fetch. Expired cached tokens are likewise refetched via
    // refetchOnMount, and foreground transitions are handled below.

    if (Platform.OS === 'web') {
      const handleVisibilityChange = () => {
        if (
          typeof document !== 'undefined' &&
          document.visibilityState === 'visible'
        ) {
          handleActiveState();
        }
      };
      // visibilitychange alone misses window-blur returns (e.g. DevTools or
      // another app holding focus): the tab stays 'visible' while blurred, so
      // a skipped refresh tick would never be caught up. Window focus covers
      // those returns. Both listeners share past-due-gated handleActiveState,
      // and concurrent refetches for the same key are deduped downstream, so
      // a return that fires both events still produces a single request.
      const handleWindowFocus = () => {
        handleActiveState();
      };
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }
      if (typeof window !== 'undefined') {
        window.addEventListener('focus', handleWindowFocus);
      }
      return () => {
        if (typeof document !== 'undefined') {
          document.removeEventListener(
            'visibilitychange',
            handleVisibilityChange,
          );
        }
        if (typeof window !== 'undefined') {
          window.removeEventListener('focus', handleWindowFocus);
        }
      };
    } else {
      const subscription = AppState.addEventListener(
        'change',
        (nextAppState) => {
          if (nextAppState === 'active') {
            handleActiveState();
          }
        },
      );
      return () => {
        if (subscription && typeof subscription.remove === 'function') {
          subscription.remove();
        }
      };
    }
  }, [query.data, query.refetch]);

  // react-query v5 dropped useQuery's onError callback, so error tracking
  // has to observe the query result instead of hooking into the query itself.
  useEffect(() => {
    if (!query.isError) return;

    // authApiService.web.ts already logs these itself right before throwing
    // (via fireGtmEvent, since it has no useLogger() access there) - on web,
    // skip them here to avoid a duplicate log. authApiService.native.ts has
    // the same no-hook-access problem, but no equivalent non-hook dispatch
    // it can call (its real logger lives in apps/move-app, which
    // packages/flights can't import) - so on native, log it right here
    // instead, using the same classification the web side uses, rather than
    // falling through to the generic classifyServiceError/UNKNOWN path.
    if (query.error instanceof Error) {
      const authRequestFailedMatch = query.error.message.startsWith(
        AUTH_REQUEST_FAILED_MESSAGE_PREFIX,
      );
      const invalidResponseMatch =
        query.error.message === AUTH_INVALID_RESPONSE_MESSAGE;
      const loginBackedOffMatch =
        query.error.message === AUTH_LOGIN_BACKED_OFF_MESSAGE;

      if (
        authRequestFailedMatch ||
        invalidResponseMatch ||
        loginBackedOffMatch
      ) {
        if (Platform.OS !== 'web') {
          logError(
            apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
            errorTrackingEvent({
              errorType: authRequestFailedMatch ? 'api' : 'validation',
              errorDescription: authRequestFailedMatch
                ? AUTH_REQUEST_FAILED_DESCRIPTION
                : invalidResponseMatch
                  ? AUTH_INVALID_RESPONSE_DESCRIPTION
                  : AUTH_LOGIN_BACKED_OFF_DESCRIPTION,
              errorMessage: query.error.message,
              screenName,
              service: apiErrorMeta.SERVICE_NAMES.FLIGHTS_AUTHENTICATION,
            }),
          );
        }
        return;
      }
    }

    const { errorType, errorCode, errorDescription, errorMessage } =
      classifyServiceError(query.error);

    logError(
      apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
      errorTrackingEvent({
        errorType,
        errorCode,
        errorDescription,
        errorMessage,
        screenName,
        service: apiErrorMeta.SERVICE_NAMES.FLIGHTS_AUTHENTICATION,
      }),
    );
  }, [query.isError, query.error, screenName, logError]);

  return { ...query, isTokenValid };
};
